// Organization > Workshops: every workshop, with the dealer and region it
// belongs to. A workshop that has customer conversations cannot be deleted,
// so it is paused instead.

import { useMemo, useState } from "react";
import { useTenantOrganization } from "../../lib/api";
import {
  filterWorkshops,
  indexOrg,
  regionNamesForDealer,
  workshopRows,
} from "../../lib/organization";
import { FieldError, FieldLabel, Select, inputClass } from "./primitives";
import WorkshopDetail from "./WorkshopDetail";
import { useWorkshopEditor } from "./useOrgEditors";
import {
  Cell,
  ConfirmDelete,
  EmptyState,
  ErrorState,
  FilterSelect,
  FormDialog,
  Loading,
  PageIntro,
  SearchBox,
  StatusChip,
  Table,
  Toolbar,
} from "./OrgKit";

export default function Workshops({ tenantId, readOnly = false }) {
  const { data, isLoading, isError, error, refetch } = useTenantOrganization(tenantId);
  const [filters, setFilters] = useState({ query: "", region: "all", dealer: "all", status: "all" });
  // The workshop being looked at, or null for the list.
  const [openId, setOpenId] = useState(null);

  const ix = useMemo(() => indexOrg(data), [data]);
  const {
    adding,
    setAdding,
    form,
    setForm,
    errors,
    openAdd: openAddUnder,
    submitAdd,
    toggleStatus,
    pending,
    deleting,
    setDeleting,
    askDelete,
    confirmDelete,
  } = useWorkshopEditor(tenantId, ix);
  const rows = useMemo(() => workshopRows(ix), [ix]);
  const shown = useMemo(() => filterWorkshops(rows, filters), [rows, filters]);

  if (isLoading) return <Loading />;
  if (isError) return <ErrorState error={error} onRetry={refetch} />;

  const open = openId ? ix.workshopById.get(openId) : null;
  if (open) {
    return <WorkshopDetail tenantId={tenantId} ix={ix} workshop={open} onBack={() => setOpenId(null)} />;
  }

  const set = (patch) => setFilters((f) => ({ ...f, ...patch }));

  const regionOptions = [
    { value: "all", label: "Any region" },
    ...ix.regions.map((r) => ({ value: r.name, label: r.name })),
  ];
  const dealerOptions = [
    { value: "all", label: "Any dealer" },
    ...ix.dealers.map((d) => ({ value: d.name, label: d.name })),
  ];

  // The button hands over its click event, which is not a dealer to start on.
  const openAdd = () => openAddUnder();

  const noneAtAll = rows.length === 0;

  return (
    <>
      <PageIntro
        title="Workshops"
        actionLabel={!readOnly && ix.dealers.length ? "Add workshop" : null}
        onAction={openAdd}
      />

      {!noneAtAll && (
        <Toolbar count={`${shown.length} of ${rows.length}`}>
          <SearchBox
            value={filters.query}
            onChange={(v) => set({ query: v })}
            placeholder="Search workshops"
            data-testid="workshop-search"
          />
          <FilterSelect
            value={filters.region}
            onChange={(v) => set({ region: v })}
            options={regionOptions}
            aria-label="Region"
          />
          <FilterSelect
            value={filters.dealer}
            onChange={(v) => set({ dealer: v })}
            options={dealerOptions}
            aria-label="Dealer"
          />
          <FilterSelect
            value={filters.status}
            onChange={(v) => set({ status: v })}
            options={[
              { value: "all", label: "Any status" },
              { value: "active", label: "Active" },
              { value: "paused", label: "Paused" },
            ]}
            aria-label="Status"
          />
        </Toolbar>
      )}

      {noneAtAll ? (
        <EmptyState
          title="No workshops yet"
          body={
            ix.dealers.length
              ? "Add the first one."
              : "No dealers yet. Add a region and a dealer first."
          }
          actionLabel={!readOnly && ix.dealers.length ? "Add workshop" : null}
          onAction={openAdd}
        />
      ) : shown.length === 0 ? (
        <EmptyState title="Nothing matches those filters" body="Clear a filter, or add the workshop that should be here." />
      ) : (
        <Table columns={["Workshop", "Dealer", "Region", "People", "Status"]}>
          {shown.map((row) => (
            <tr key={row.id} data-testid="workshop-row">
              <Cell>
                <button
                  type="button"
                  onClick={() => setOpenId(row.id)}
                  className="text-left text-[14px] font-medium text-[#111827] hover:text-[#2667B5] hover:underline"
                >
                  {row.location}
                </button>
              </Cell>
              <Cell>{row.dealer || "—"}</Cell>
              <Cell>{row.region || "—"}</Cell>
              <Cell>{row.users}</Cell>
              <Cell>
                <button type="button" onClick={() => toggleStatus(row)} title="Pause or resume this workshop">
                  <StatusChip status={row.status} />
                </button>
              </Cell>
              <Cell className="whitespace-nowrap text-right">
                {!readOnly && (
                  <>
                <button
                  type="button"
                  onClick={() => askDelete(row.id)}
                  aria-label={`Delete ${row.location}`}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-[#9CA3AF] hover:bg-[#FEF2F2] hover:text-[#DC2626]"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M4 7h16M9 7V5h6v2M6 7l1 13h10l1-13" />
                  </svg>
                </button>
                  </>
                )}
              </Cell>
            </tr>
          ))}
        </Table>
      )}

      <FormDialog
        open={adding}
        onOpenChange={setAdding}
        title="Add workshop"
        onSubmit={submitAdd}
        submitLabel="Add workshop"
        pending={pending}
      >
        <div>
          <FieldLabel>Workshop name</FieldLabel>
          <input
            type="text"
            className={inputClass}
            value={form.workshop_location}
            placeholder="Bandra"
            autoFocus
            onChange={(e) => setForm({ ...form, workshop_location: e.target.value })}
          />
          <FieldError>{errors.workshop_location}</FieldError>
        </div>
        <div>
          <FieldLabel>Dealer</FieldLabel>
          <Select value={form.dealer_id} onChange={(e) => setForm({ ...form, dealer_id: e.target.value })}>
            {ix.dealers.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
                {regionNamesForDealer(d, ix).length ? ` · ${regionNamesForDealer(d, ix).join(", ")}` : ""}
              </option>
            ))}
          </Select>
          <FieldError>{errors.dealer_id}</FieldError>
        </div>
      </FormDialog>

      <ConfirmDelete
        open={!!deleting}
        onOpenChange={(v) => !v && setDeleting(null)}
        name={deleting?.name || ""}
        body={deleting?.note || "This cannot be undone."}
        blocked={deleting?.blocked}
        onConfirm={confirmDelete}
        pending={pending}
      />
    </>
  );
}
